import React from "react"

import {Box} from "@mui/material"

import {ErrorResponse, Loading, RestResponse,} from "@sinossi/mates-react-library"

import {
    Action,
    ActionsRequest,
    AssignableUsers,
    AssignRequest,
    NewStepRequest,
    PaginatedResponse,
    SessionResponse,
} from "models"
import withUseRouter, {UseRouterParams} from "services/router"
import {PageHeader, RoutesData,} from "components/commons"
import {MatesComponent, MatesComponentState,} from "components/mates-component"
import {ActionsServices} from "services/actions.services"
import {ActionsComponent,} from "components/actions"
import {WorkflowService} from "../../services/workflow.service";
import {AssignWorkflowAction, DeferAction, NewStep} from "../../components/workflow/modals";
import {MarkActionAsDone} from "../../components/workflow/modals/mark-action-as-done";
import {SbSnackbar} from "../../components/commons/sb-snackbar";
import {handleDeferErrorMessages} from "../../components/workflow/modals/defer-action/defer-error-messages";
import {SkipActionDialog} from "../../components/workflow/modals/skip-action-dialog";

interface ActionsProps extends UseRouterParams
{
    sessionData: SessionResponse
}
interface ActionsState extends MatesComponentState
{
    loading: boolean
    urgentActions: PaginatedResponse<Action> | null
    urgentRequest: ActionsRequest
    programmedActions: PaginatedResponse<Action> | null
    programmedRequest: ActionsRequest
    historyActions: PaginatedResponse<Action> | null
    historyRequest: ActionsRequest
    actionType: string
    actions: boolean
    openDeferAction: boolean
    openMarkAsDone: boolean
    openAssignAction: boolean
    openSkipAction: boolean
    selectedAction: Action | null
    openCreateAction: boolean
    snackbarData: {
        openNotification: boolean,
        severity: 'error' | 'info' | 'success' | 'warning',
        notificationMessage: string
    }
    assignableUsers: AssignableUsers[] | null
}
class Actions extends React.Component<ActionsProps, ActionsState>
{
    private handleDeferError = (details: { [id: string]: string }|null, response: RestResponse<any, ErrorResponse>) => {
        const errorMessage = handleDeferErrorMessages(details)
        this.handleOpenNotification(errorMessage.message, errorMessage.severity)
        this.setState(() => ({
            loading: false
        }))
    }

    private readonly matesComponent: MatesComponent<ActionsProps, ActionsState> = new MatesComponent(this, this.handleDeferError)

    constructor(props: ActionsProps)
    {
        super(props);
        this.state = {
            ...this.matesComponent.setupState(),
            loading: true,
            urgentActions: null,
            urgentRequest: ActionsRequest.empty(),
            programmedActions: null,
            programmedRequest: ActionsRequest.empty(),
            historyActions: null,
            historyRequest: ActionsRequest.empty(),
            actionType: "URGENT",
            actions: true,
            openDeferAction: false,
            openMarkAsDone: false,
            openAssignAction: false,
            openSkipAction: false,
            selectedAction: null,
            openCreateAction: false,
            snackbarData: {
                openNotification: false,
                severity: "info",
                notificationMessage: ""
            },
            assignableUsers: null
        }
    }

    componentDidMount()
    {
        this.getUrgentActions()
    }

    private getUrgentActions = (): void => {
        this.setState(() => ({
            loading: true
        }), () =>
            ActionsServices.getUrgentActions(
                this.state.urgentRequest,
                this.getUrgentActionsCallback,
                this.matesComponent.onException
            )
        )

    }

    private getUrgentActionsCallback = (response: RestResponse<PaginatedResponse<Action>, ErrorResponse>): void => {
        if(response.hasError()) {
            this.matesComponent.onException(response)
            return
        }
        this.setState(
            () => ({urgentActions: response.data}),
            this.getProgrammedActions
        )
    }

    private getProgrammedActions = (): void => {
        this.setState(() => ({
            loading: true
        }), () => ActionsServices.getProgrammedActions(
            this.state.programmedRequest,
            this.getProgrammedActionsCallback,
            this.matesComponent.onException
        ))
    }

    private getProgrammedActionsCallback = (response: RestResponse<PaginatedResponse<Action>, ErrorResponse>): void => {
        if(response.hasError()) {
            this.matesComponent.onError(response)
            return
        }

        this.setState(() => ({
            loading: false,
            programmedActions: response.data,
        }))
    }

    private getHistory = (): void => {
        this.setState(() => ({
            loading: true
        }), () => ActionsServices.getHistory(
            this.state.historyRequest,
            this.getHistoryCallback,
            this.matesComponent.onException
        ))
    }

    private getHistoryCallback = (response: RestResponse<PaginatedResponse<Action>, ErrorResponse>): void => {
        if(response.hasError()) {
            this.matesComponent.onError(response)
            return
        }

        this.setState(() => ({
            loading: false,
            historyActions: response.data,
        }))
    }

    private handlePaginationFirstColumn = (actionType: string, pageId: number): void => {
        if(actionType === "URGENT") {
            const request = {
                ...this.state.urgentRequest,
                pageId: pageId - 1,
            }

            this.setState(() => ({
                urgentRequest: request,
            }), this.getUrgentActions)
        }
        else if (actionType === "PROGRAMMED"){
            const request = {
                ...this.state.programmedRequest,
                pageId: pageId - 1,
            }

            this.setState(
                () => ({ programmedRequest: request }),
                this.getProgrammedActions
            )
        }
        else {
            return
        }
    }

    private onSectionChanged = (kind: string | null): void => {
        this.setState(
            () => ({loading: true}),
            () => this.selectApi(kind)
        )
    }

    private selectApi = (kind: string|null): void => {
        if(kind === "ACTIONS") {
            this.getUrgentActions()
        }
        else if (kind === "HISTORY") {
            this.getHistory()
        }
    }

    private handleHistoryPagination = (pageId: number): void => {
        this.setState(() => ({
            historyRequest: {
                ...this.state.historyRequest,
                pageId: pageId - 1,
            },
        }), this.getHistory)
    }

    private handleSearchbarCallback = (data: ActionsRequest): void => {
        this.setState(() => ({
            loading: true,
            urgentRequest: data
        }), this.getUrgentActions)
    }

    private openDeferDialog = (data: Action) => {
        this.setState(() => ({
            openDeferAction: true,
            selectedAction: data
        }))
    }

    private closeDeferDialog = () => {
        this.setState(() => ({
            openDeferAction: false,
        }))
    }

    private submitDeferAction = (values: any) => {
        if(!this.state.selectedAction) {
            return
        }

        const request: { deadline: string } = {deadline: values.deadline}
        const action: Action = this.state.selectedAction

        WorkflowService.deferAction(action.id, request, this.submitDeferCallback, this.matesComponent.onError)
    }

    private submitDeferCallback = (response: RestResponse<any, ErrorResponse>) => {
        if(response.hasError()) {
            this.matesComponent.onError(response)
            return
        }
        this.closeDeferDialog()
        this.getUrgentActions()
    }

    private handleOpenNotification = (message: string, severity: 'error' | 'info' | 'success' | 'warning') => {
        this.setState(() => ({
            snackbarData: {
                openNotification: true,
                notificationMessage: message,
                severity: severity
            },
        }))
    };

    private handleCloseNotification = () => {
        this.setState(() => ({
            snackbarData: {
                ...this.state.snackbarData,
                openNotification: false
            }
        }))
    };

    private openMarkAsDoneDialog = (data: Action) => {
        this.setState(() => ({
            openMarkAsDone: true,
            selectedAction: data
        }))
    }

    private closeMarkAsDoneDialog = () => {
        this.setState(() => ({
            openMarkAsDone: false,
        }))
    }

    private submitMarkAsDone = () => {
        if(!this.state.selectedAction) {
            return
        }

        WorkflowService.markAsDone(this.state.selectedAction.id, this.submitMarkAsDoneCallback, this.matesComponent.onException)
    }

    private submitMarkAsDoneCallback = (response: RestResponse<any, ErrorResponse>) => {
        if(response.hasError()) {
            this.matesComponent.onError(response)
            return
        }

        this.closeMarkAsDoneDialog()
        this.getUrgentActions()
    }

    private openCreateAction = () => {
        this.setState(() => ({
            openCreateAction: true
        }))
    }

    private closeCreateAction = () => {
        this.setState(() => ({
            openCreateAction: false
        }))
    }

    private createAction = (value: NewStepRequest) => {
        ActionsServices.createNewAction(value, this.createActionCallback, this.matesComponent.onError)
    }

    private createActionCallback = (response: RestResponse<any, ErrorResponse>) => {
        if(response.hasError()) {
            this.matesComponent.onError(response)
            return
        }

        this.closeCreateAction()
        this.getUrgentActions()
    }

    private openAssignAction = (action: Action) => {
        this.setState(() => ({
            selectedAction: action,
            openAssignAction: true,
        }), () => this.getAssignableUsers(action.id))
    }

    private closeAssignAction = () => {
        this.setState(() => ({
            selectedAction: null,
            openAssignAction: false
        }))
    }

    private assignAction = (actionId: string, userId: string) => {
        let request: AssignRequest = {
            executor: userId
        }
        WorkflowService.assignWorkflowAction(actionId, request, this.assignWorkflowActionCallback, this.matesComponent.onError)
    }

    private assignWorkflowActionCallback = (response: RestResponse<any, ErrorResponse>) => {
        if (response.hasError()) {
            this.matesComponent.onError(response)
            return
        }

        this.closeAssignAction()
        this.getUrgentActions()
    }

    private getAssignableUsers = (actionId: string) => {
        this.setState(() => ({
            loading: true
        }), () => WorkflowService.getAssignableUsers(actionId, this.getAssignableUsersCallback, this.matesComponent.onException))
    }

    private getAssignableUsersCallback = (response: RestResponse<AssignableUsers[], ErrorResponse>) => {
        if (response.hasError()) {
            this.matesComponent.onError(response)
            return
        }

        this.setState(() => ({
            assignableUsers: response.data,
            loading: false
        }))
    }

    private actionContentChanged = (updatedAction: Action) => {
        if(this.state.urgentActions){
            this.state.urgentActions.items.map((action, index) => {
                if(action.id === updatedAction.id){
                    const newAction: Action[] = this.state.urgentActions!.items
                    newAction[index] = updatedAction
                    this.setState(() => ({
                        urgentActions: {
                            ...this.state.urgentActions!,
                            items: newAction
                        }
                    }))
                    return
                }
            })
        }
        if(this.state.programmedActions){
            this.state.programmedActions.items.map((action, index) => {
                if(action.id === updatedAction.id){
                    const newAction: Action[] = this.state.programmedActions!.items
                    newAction[index] = updatedAction
                    this.setState(() => ({
                        programmedActions: {
                            ...this.state.programmedActions!,
                            items: newAction
                        }
                    }))
                }
            })
        }
    }

    private openSkipDialog = (action: Action) => {
        this.setState(() => ({
            openSkipAction: true,
            selectedAction: action
        }))
    }

    private closeSkipDialog = () => {
        this.setState(() => ({
            openSkipAction: false,
        }))
    }

    private skipAction = () => {
        this.setState(() => ({
            loading: true,
        }), () => WorkflowService.skipAction(this.state.selectedAction!.id, this.skipActionCallback, this.matesComponent.onError))
    }

    private skipActionCallback = () => {
        this.closeSkipDialog()
        if(this.state.actionType === "URGENT"){
            this.setState(() => ({
                selectedAction: null
            }), this.getUrgentActions)

        }
        else {
            this.setState(() => ({
                selectedAction: null
            }), this.getProgrammedActions)
        }
    }

    private updateActiveSection = (actionType: string) => {
        this.setState(() => ({
            actionType: actionType
        }))
    }

    render()
    {
        const isBrand: boolean = this.props.sessionData.accountType === "BRAND"

        return (
            <Box sx={{pt: 2}}>
                <Loading show={this.state.loading} />
                {
                    isBrand ?
                        <PageHeader
                            title={RoutesData.ACTIONS.title}
                            paths={[ RoutesData.DASHBOARD, RoutesData.ACTIONS, ]}
                        />
                        :
                        <PageHeader
                            title={RoutesData.ACTIONS.title}
                            paths={[ RoutesData.DASHBOARD, RoutesData.ACTIONS, ]}
                            addButtonModal={"Aggiungi Azione"}
                            onClickButtonModal={this.openCreateAction}
                        />
                }

                {/*<ActionsAdvancedSearch handleCallback={this.handleSearchbarCallback}/>*/}

                <ActionsComponent
                    urgentData={this.state.urgentActions}
                    programmedData={this.state.programmedActions}
                    sessionData={this.props.sessionData}
                    selectedAction={this.state.selectedAction}
                    historyData={this.state.historyActions}
                    updateSection={this.updateActiveSection}
                    handlePaginationFirstColumn={this.handlePaginationFirstColumn}
                    handleHistoryPagination={this.handleHistoryPagination}
                    openDeferAction={this.openDeferDialog}
                    openMarkAsDone={this.openMarkAsDoneDialog}
                    openAssignAction={this.openAssignAction}
                    openSkipDialog={this.openSkipDialog}
                    onSectionChanged={this.onSectionChanged}
                    onException={this.matesComponent.onException}
                    refresh={this.getUrgentActions}
                    updateAction={this.actionContentChanged}
                />

                {
                    this.state.openDeferAction && this.state.selectedAction &&
                    <DeferAction
                        openDialog={this.state.openDeferAction}
                        onClose={this.closeDeferDialog}
                        data={this.state.selectedAction}
                        submit={this.submitDeferAction}
                    />
                }
                {
                    this.state.openMarkAsDone && this.state.selectedAction &&
                    <MarkActionAsDone onClose={this.closeMarkAsDoneDialog}
                                      action={this.state.selectedAction}
                                      submit={this.submitMarkAsDone}
                    />
                }
                {
                    this.state.openCreateAction ?
                    <NewStep action
                             openDialog={this.state.openCreateAction}
                             onClose={this.closeCreateAction}
                             create
                             submit={this.createAction}
                    />
                    :
                    null
                }
                {
                    !this.state.loading && this.state.openAssignAction && this.state.selectedAction &&
                    <AssignWorkflowAction openDialog={this.state.openAssignAction}
                                          onClose={this.closeAssignAction}
                                          assign={this.assignAction}
                                          data={this.state.selectedAction}
                                          users={this.state.assignableUsers ?? []}/>
                }
                {
                    this.state.openSkipAction && this.state.selectedAction &&
                    <SkipActionDialog onClose={this.closeSkipDialog}
                                      skip={this.skipAction} />
                }
                <SbSnackbar snackbarData={this.state.snackbarData} handleCloseNotification={this.handleCloseNotification} />
            </Box>
        )
    }
}

export default withUseRouter(Actions)
